import type { HistoryEntry, VersionMeta } from './types'

/**
 * One row of the History modal (AB#241), newest first:
 * - an `entry` of the room's live log (Y.Array('history'), 50 entries at most), with the restore
 *   point the store hook tagged with it when it still exists;
 * - an older `version`: a restore point older than the log, described by what it froze of its entry.
 * `merged` = how many changes this row stands for on top of its own whose restore point was removed
 * (by a flatten or by retention) — shown as "3 changes merged" instead of as dead rows.
 */
export type HistoryRow =
  | { type: 'entry'; key: string; entry: HistoryEntry; version?: VersionMeta; merged: number }
  | { type: 'version'; key: string; version: VersionMeta; merged: number }

/**
 * Build the modal's rows from the log (`entries`, oldest first, as the CRDT holds them) and the
 * restore points read so far (`versions`, newest first — GET /v1/versions pages, appended).
 *
 * A log entry whose restore point is gone is FOLDED into the next newer row when it cannot be
 * restored any other way (`restorable` false) and restore points were being taken when it was
 * written (a version older than it exists) — that is a removed restore point, not an entry from
 * before restore points existed. The newest entry never folds: its restore point lands a moment
 * after it.
 *
 * Older versions are the ones written before the log's oldest entry and tied to none of its entries.
 * Their `merged` is the gap in the numbering below them (numbers are allocated max + 1 per room and
 * the newest is never removed, so a gap is exactly the removed ones — backend/domain/versions.ts);
 * below the oldest version read, it is only known once there is no older page (`complete`).
 */
export function buildHistoryRows(
  entries: HistoryEntry[],
  versions: VersionMeta[],
  opts: { restorable: (entry: HistoryEntry) => boolean; complete: boolean },
): HistoryRow[] {
  const byHistoryId = new Map<string, VersionMeta>()
  for (const v of versions) if (v.historyId) byHistoryId.set(v.historyId, v)
  const oldestTaken = versions.reduce<number>(
    (min, v) => (v.historyId ? Math.min(min, Date.parse(v.createdAt)) : min),
    Infinity,
  )

  const rows: HistoryRow[] = []
  const newestFirst = [...entries].reverse()
  newestFirst.forEach((entry, idx) => {
    const version = byHistoryId.get(entry.id)
    const removed =
      idx > 0 && !version && !opts.restorable(entry) && oldestTaken < Date.parse(entry.createdAt)
    const previous = rows[rows.length - 1]
    if (removed && previous) {
      previous.merged += 1
      return
    }
    rows.push({ type: 'entry', key: entry.id, entry, ...(version ? { version } : {}), merged: 0 })
  })

  const inLog = new Set(entries.map((e) => e.id))
  const logStart = entries.length ? Math.min(...entries.map((e) => Date.parse(e.createdAt))) : Infinity
  const older = versions
    .filter((v) => !(v.historyId && inLog.has(v.historyId)) && Date.parse(v.createdAt) < logStart)
    .sort((a, b) => b.version - a.version)
  older.forEach((v, i) => {
    const next = older[i + 1]
    const below = next ? v.version - next.version - 1 : opts.complete ? v.version - 1 : 0
    rows.push({ type: 'version', key: `v${v.version}`, version: v, merged: Math.max(0, below) })
  })
  return rows
}
